import React, { FunctionComponent } from "react";
import { Node, Edge, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { BannerStatus } from "@plex/react-components";
import { INodeConfigPropertiesProps } from "../NodeTypes/PropertyTypeDefinitions";
import {
  addListSchemaIfNotExists,
  emptyObjectSchemaId,
  getSchema,
  listOfEmptyObjectSchemaId,
  noReferenceListSchemaId,
  noReferenceSchemaId,
  setObjectSchemas
} from "../NodeTypes/DataSchemas";
import { IDataParam, INodeProperty, ISchemaPickerOptions, ISchemaPropertyPickerOptions } from "../NodeTypes/Base";
import { DataType } from "../NodeTypes/TypeDefinitions";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";
import { isDynamicObjectSchema } from "../FlowDocument/FlowDocumentModel";
import { getPickerSchemaProperties } from "./SchemaPropertyPickerField";
import { getDataPropertyCountConfigForProperty } from "./DataPropertyCountFormField";
import {
  buildNodeInputsFromSchema,
  createDataParamsFromSchema,
  deleteEdgesFromNodes,
  getNameFromDataHandleId
} from "../Util/EdgeUtil";
import { IViewController, useViewController } from "../ViewContext";
import { SchemaPicker } from "./SchemaPicker";

export interface ISchemaPickerProps extends INodeConfigPropertiesProps {
  dialogTitle: string;
  displayName: string;
}

export const setLinkedSchemaPropertySelectionToDefault = (node: Node, propertyName: string): string | undefined => {
  let firstProperty: string | undefined = undefined;
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node!.type!);
  const linkedSchemaProperties = nodeDefinition?.nodeConfigProperties.filter(
    (configProperty: INodeProperty) =>
      configProperty.propertyType === NodeConfigPropertyType.SchemaProperty &&
      (configProperty.options as ISchemaPropertyPickerOptions)?.schemaIdProperty === propertyName
  );

  linkedSchemaProperties?.forEach((property: INodeProperty) => {
    const propertyOptions = property.options as ISchemaPropertyPickerOptions;
    const pickerProperties = getPickerSchemaProperties(node, propertyOptions);
    const oldPropertyIsInPicker =
      node!.data.nodeProperties[property.name] &&
      pickerProperties.some((p) => p.id === node!.data.nodeProperties[property.name]);
    if (!node!.data.nodeProperties[property.name] || !oldPropertyIsInPicker) {
      const firstPickerProperty = pickerProperties.length > 0 ? pickerProperties[0] : undefined;
      if (propertyOptions?.displayProperty) {
        node!.data.nodeProperties[propertyOptions.displayProperty] = firstPickerProperty?.name;
      }
      node!.data.nodeProperties[property.name] = firstPickerProperty?.id;
      firstProperty = firstPickerProperty?.id;
    } else {
      firstProperty = node!.data.nodeProperties[property.name];
    }
  });

  return firstProperty;
};

export const SchemaPickerField: FunctionComponent<ISchemaPickerProps> = (props) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const viewController: IViewController = useViewController();
  const options = props.options as ISchemaPickerOptions;

  const onChange = (values: { id: string; name: string }[]) => {
    if (props.node!.data.nodeProperties[props.name] === values[0]?.id) {
      return;
    }

    if (values.length > 0 && options.changeSchemaOnConnection) {
      addListSchemaIfNotExists(values[0]?.id ?? "", props.node, options.dataProperties);
    }

    // A dynamic object is mapped into the selected schema rather than schema-matched, so an
    // edge carrying one survives a schema change. This is what keeps the "Fields" node's
    // object connection when its "As Object" schema selection changes for example.
    const excludeDynamicObjectEdge = (e: Edge): boolean => {
      const sourceNode = reactFlowInstance.getNode(e.source);
      const targetNode = reactFlowInstance.getNode(e.target);
      const sourceParam = sourceNode?.data.nodeProperties.inputs[getNameFromDataHandleId(e.sourceHandle ?? "")];
      const targetParam = targetNode?.data.nodeProperties.outputs[getNameFromDataHandleId(e.targetHandle ?? "")];
      return (
        isDynamicObjectSchema(getSchema(sourceParam?.schemaId)) ||
        isDynamicObjectSchema(getSchema(targetParam?.schemaId))
      );
    };

    let currentEdges: Edge[] = reactFlowInstance.getEdges();
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(props.node!.type!);
    const linkedInputNames = Object.keys(props.node.data.nodeProperties.inputs).filter((inputName) =>
      options.dataProperties.some(
        (dp) =>
          inputName === dp ||
          (!!getDataPropertyCountConfigForProperty(nodeDefinition, dp) && inputName.startsWith(dp + "_"))
      )
    );
    const linkedOutputNames = Object.keys(props.node.data.nodeProperties.outputs).filter((outputName) =>
      options.dataProperties.includes(outputName)
    );

    // An object mapping input (`IDataParam.mapsObject`, mirroring the backend's
    // `DataInputModel.MapsObject`) accepts any object schema and maps it into the node's target
    // schema, so its connection stays valid when the schema selection changes.
    const objectMappingInputNames = (nodeDefinition?.dataInputs ?? [])
      .filter((input: IDataParam) => input.mapsObject)
      .map((input: IDataParam) => input.name);
    const isObjectMappingInputEdge = (e: Edge): boolean =>
      e.source === props.node.id && objectMappingInputNames.includes(getNameFromDataHandleId(e.sourceHandle ?? ""));

    const excludeEdge = (e: Edge): boolean => excludeDynamicObjectEdge(e) || isObjectMappingInputEdge(e);

    const inputsSchemaProperties = !!nodeDefinition?.inputsSchemaProperties;
    const outputsSchemaProperties = !!nodeDefinition?.outputsSchemaProperties;
    if (
      linkedInputNames.length > 0 ||
      linkedOutputNames.length > 0 ||
      inputsSchemaProperties ||
      outputsSchemaProperties
    ) {
      currentEdges = deleteEdgesFromNodes(
        [props.node.id],
        reactFlowInstance,
        updateNodeInternals,
        {
          outputEdges: linkedOutputNames.length > 0 || outputsSchemaProperties,
          inputEdges: linkedInputNames.length > 0 || inputsSchemaProperties,
          controlEdges: false,
          excludeEdge
        },
        currentEdges
      );
    }

    const updateInputsFromSchema = (firstIdentifier: string | undefined, clearInputs?: boolean) => {
      const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(props.node!.type!)!;
      if (nodeDefinition.inputsSchemaProperties?.schemaPropertyName === props.name) {
        currentEdges = deleteEdgesFromNodes(
          [props.node.id],
          reactFlowInstance,
          updateNodeInternals,
          {
            outputEdges: false,
            inputEdges: true,
            controlEdges: false,
            excludeEdge
          },
          currentEdges
        );
        if (clearInputs) {
          const staticInputs: Record<string, IDataParam> = {};
          nodeDefinition.dataInputs.forEach((i: IDataParam) => {
            staticInputs[i.name] = i;
          });
          props.node.data.nodeProperties.inputs = { ...staticInputs };
        } else {
          props.node.data.nodeProperties.inputs = buildNodeInputsFromSchema(
            props.node,
            values[0]?.id ?? "",
            nodeDefinition,
            reactFlowInstance,
            options.dataProperties,
            values[0]?.id,
            firstIdentifier
          );
        }
      }
    };

    const updateOutputsFromSchema = () => {
      const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(props.node!.type!)!;
      const isSchemaSource = nodeDefinition.nodeConfigProperties.some(
        (p: INodeProperty) => p.schemaSourceSystemType !== undefined
      );
      if (nodeDefinition.outputsSchemaProperties && !isSchemaSource) {
        const schemaId = values[0]?.id;
        if (schemaId) {
          props.node.data.nodeProperties.outputs = createDataParamsFromSchema(
            props.node,
            schemaId,
            false,
            reactFlowInstance
          );
        } else {
          props.node.data.nodeProperties.outputs = {};
        }
      }
    };

    if (values.length === 0) {
      props.node!.data.nodeProperties[props.name] = null;
      if (options?.displayProperty) {
        props.node!.data.nodeProperties[options.displayProperty] = null;
      }

      const setEmptyObjectSchemas = (dataPropertyNames: string[], dataProperties: IDataParam[]) => {
        dataPropertyNames.forEach((dataPropertyName) => {
          const dataProperty = dataProperties[dataPropertyName];
          switch (dataProperty.type) {
            case DataType.OBJECT:
              dataProperty.schemaId = emptyObjectSchemaId;
              break;
            case DataType.OBJECTREFERENCE:
              dataProperty.schemaId = noReferenceSchemaId;
              break;
            case DataType.OBJECTREFERENCELIST:
              dataProperty.schemaId = noReferenceListSchemaId;
              break;
            case DataType.OBJECTLIST:
              dataProperty.schemaId = listOfEmptyObjectSchemaId;
              break;
          }
        });
      };

      setEmptyObjectSchemas(linkedInputNames, props.node.data.nodeProperties.inputs);
      setEmptyObjectSchemas(linkedOutputNames, props.node.data.nodeProperties.outputs);
      const firstIdentifier = setLinkedSchemaPropertySelectionToDefault(props.node, props.name);
      updateInputsFromSchema(firstIdentifier, true);
      updateOutputsFromSchema();

      if (props.onChange) {
        props.onChange();
      }

      props.forceNodeUpdate();

      return;
    }

    props.node!.data.nodeProperties[props.name] = values[0]?.id;
    if (options?.displayProperty) {
      props.node!.data.nodeProperties[options.displayProperty] = values[0]?.name;
    }

    let setSchemaErrors = setObjectSchemas(
      values[0]?.id ?? "",
      linkedInputNames,
      props.node.data.nodeProperties.inputs
    );
    setSchemaErrors = setSchemaErrors.concat(
      setObjectSchemas(values[0]?.id ?? "", linkedOutputNames, props.node.data.nodeProperties.outputs)
    );
    if (setSchemaErrors.length > 0) {
      viewController.bannerContext.addMessage(setSchemaErrors[0], BannerStatus.warning);
    }

    const firstIdentifier = setLinkedSchemaPropertySelectionToDefault(props.node, props.name);
    updateInputsFromSchema(firstIdentifier);
    updateOutputsFromSchema();

    if (props.onChange) {
      props.onChange();
    }

    props.forceNodeUpdate();
  };

  return (
    <div>
      <SchemaPicker
        key={props.node.data.nodeProperties[props.name] ?? "empty"}
        dialogTitle={"Document Schemas"}
        showEditObjectsButton={options.referenceOnly ? false : true}
        initialSelection={
          props.node?.data.nodeProperties[props.name]
            ? {
                id: props.node.data.nodeProperties[props.name],
                name: options.displayProperty ? props.node.data.nodeProperties[options.displayProperty] : ""
              }
            : undefined
        }
        options={options}
        onChange={onChange}
      />
    </div>
  );
};
